import React from 'react';
import { arrivalLineColor } from '../../../constants/colors';
import AdmNivoChart from '../adm_executive_view/AdmNivoChart';

// Helper to cumulate datapoints
const cumulateDatapoints = (datapoints) => {
  const cumulated = [];
  if (datapoints && datapoints.length > 0) {
    for (let i = 0; i < datapoints.length; i++) {
      cumulated.push({
        interval: datapoints[i].interval,
        value: datapoints[i].value,
        date: datapoints[i].date
      });
      if (i > 0) {
        cumulated[i].value += cumulated[i - 1].value;
      }
    }
  }
  return cumulated;
};

// Helper to find interval for a given date
const findIntervalForDate = (datapoints, targetDate) => {
  if (!datapoints || !targetDate) return null;

  const targetDateStr = new Date(targetDate).toISOString().substring(0, 10);

  for (let i = 0; i < datapoints.length; i++) {
    const dataDateStr = new Date(datapoints[i].date).toISOString().substring(0, 10);
    if (dataDateStr === targetDateStr) {
      return datapoints[i].interval;
    }
  }
  return null;
};

export default function AdmStabilityChart(props) {
  const { raw_response, increment, d0_date, d2_date, predicted_defects_at_d0 } = props;

  if (!raw_response || !raw_response.arrival) {
    return null;
  }

  const { arrival } = raw_response;
  const lines = [];
  const rects = [];
  const scatters = [];

  // Add Arrival (Actual) - cumulative
  if (arrival.actual_processed && arrival.actual_processed.length > 0) {
    const arrivalActual = cumulateDatapoints(arrival.actual_processed);
    lines.push({
      id: 'Arrival (Actual)',
      data: arrivalActual,
      color: arrivalLineColor,
      strokeStyle: 'solid',
      legend: true
    });
  }

  // Add Arrival (Predicted) - cumulative
  if (arrival.predicted && arrival.predicted.length > 0) {
    const arrivalPredicted = cumulateDatapoints(arrival.predicted);
    lines.push({
      id: 'Arrival (Predicted)',
      data: arrivalPredicted,
      color: arrivalLineColor,
      strokeStyle: 'dashed',
      legend: true
    });
  }

  // Add stability data - Predicted @ D0 over time
  if (arrival.stability && arrival.stability.length > 0) {
    // Convert stability data to interval-based format
    const stabilityData = arrival.stability.map(point => ({
      interval: point.week || point.interval,
      value: point.predicted_at_d0,
      date: point.date
    }));

    lines.push({
      id: 'Predicted @ D0',
      data: stabilityData,
      color: 'blue',
      strokeStyle: 'solid',
      legend: true
    });
  }

  // Calculate +/- 10% limits from predicted_defects_at_d0
  if (predicted_defects_at_d0 && arrival.actual_processed && arrival.actual_processed.length > 0) {
    const targetValue = predicted_defects_at_d0;
    const beginInterval = arrival.actual_processed[0].interval;
    const endInterval = arrival.predicted && arrival.predicted.length > 0
      ? arrival.predicted[arrival.predicted.length - 1].interval
      : arrival.actual_processed[arrival.actual_processed.length - 1].interval;

    // Target line (predicted at D0)
    lines.push({
      id: 'Target',
      data: [
        { interval: beginInterval, value: targetValue },
        { interval: endInterval, value: targetValue },
      ],
      color: "limegreen",
      strokeStyle: 'dashed',
      legend: true,
      keyOrder: 10
    });

    // +10% limit
    lines.push({
      id: '+10% Limit',
      data: [
        { interval: beginInterval, value: targetValue * 1.1 },
        { interval: endInterval, value: targetValue * 1.1 },
      ],
      color: "#ffc001",
      strokeStyle: 'dashed',
      legend: true,
      keyOrder: 11
    });

    // -10% limit
    lines.push({
      id: '-10% Limit',
      data: [
        { interval: beginInterval, value: targetValue * 0.9 },
        { interval: endInterval, value: targetValue * 0.9 },
      ],
      color: "black",
      strokeStyle: 'dashed',
      legend: true,
      keyOrder: 12
    });

    // Optional: Add shaded rectangle between +/- 10% limits
    rects.push({
      id: 'Rect ~ Target +/-10%',
      data: [
        { interval: beginInterval, value: targetValue * 0.9 },
        { interval: endInterval, value: targetValue * 1.1 }
      ],
      opacity: 0.1,
    });
  }

  // Find D0 and D2 intervals
  const d0_interval = arrival.actual_processed
    ? findIntervalForDate(arrival.actual_processed, d0_date)
    : null;
  const d2_interval = arrival.actual_processed
    ? findIntervalForDate(arrival.actual_processed, d2_date)
    : null;

  return (
    <AdmNivoChart
      title='Predicted defects at D0'
      lines={lines}
      rects={rects}
      scatters={scatters}
      chartHeight='28vw'
      allowHidingLines={true}
      allowHoverTooltip={true}
      xAxisLabel={`Interval (${increment} days)`}
      d0_interval={d0_interval}
      d2_interval={d2_interval}
    />
  );
}
